import { Outlet, useLocation, useMatches, useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useEffect } from 'react';
import { SWRConfig } from 'swr';
import Loading from '@/components/shared/Loading';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import AppShell from '../shared/shell/AppShell';

interface AccountLayoutProps {
  children: React.ReactNode;
}

function LoadingComponent() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loading size="lg" />
    </div>
  );
}

export default function AccountLayout({ children }: AccountLayoutProps) {
  const { user, isLoading, login, error } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading || user) return;

    if (error) {
      void navigate({ to: '/auth/login', search: { error } });
      return;
    }

    login(`${location.pathname}${location.searchStr}`);
  }, [error, isLoading, location.pathname, location.searchStr, login, navigate, user]);

  if (isLoading || !user) {
    return <LoadingComponent />;
  }

  return (
    <SWRConfig value={{ revalidateOnFocus: false }}>
      <AppShell>{children}</AppShell>
    </SWRConfig>
  );
}

/**
 * TanStack root-route component. Renders the AccountLayout chrome around the
 * matched route's <Outlet /> unless the route opts out via
 * `staticData: { noAuth: true }` (login, OAuth callbacks, the redirect index).
 */
export function RootLayout() {
  const matches = useMatches();
  const location = useLocation();
  const noAuth = matches.some((m) => m.staticData?.noAuth);

  if (noAuth) {
    return <Outlet />;
  }

  return (
    <AccountLayout>
      {/*
        Key on pathname only (not the full search string). Keying on searchStr
        remounts this subtree on every search-param change, which tears down
        open modal UI (e.g. a Radix dropdown) mid-interaction and leaves
        `pointer-events: none` stuck on <body>, freezing the whole screen.
        The page-in animation still re-runs on real (path) navigations.
      */}
      <div key={location.pathname} className="h-full animate-page-in">
        <Outlet />
      </div>
    </AccountLayout>
  );
}
